import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Modal,
  NativeSelect,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MembershipProductsModal } from '../components/membership-products-modal';
import { NotificationPreferencesCard } from '../components/notification-preferences-card';
import { useOrgs } from '../api/orgs';
import {
  useAddPlatformUserMembership,
  useDeactivatePlatformUser,
  usePlatformUser,
  useReactivatePlatformUser,
  useRemovePlatformUserMembership,
  useResetPlatformUserPassword,
  useRolesForOrg,
  useUpdatePlatformUser,
  useUpdatePlatformUserMembershipRole,
  type PlatformUserMembership,
  type UpdatePlatformUserInput,
} from '../api/platform-users';

const STANDARD_ID_TYPES = [
  { value: 'GMC', label: 'GMC' },
  { value: 'NMC', label: 'NMC' },
  { value: 'NPI', label: 'NPI (US)' },
  { value: 'OTHER', label: 'Other' },
];

function profIdTypeOptions(currentValue: string): { value: string; label: string }[] {
  if (!currentValue || STANDARD_ID_TYPES.some((o) => o.value === currentValue)) {
    return STANDARD_ID_TYPES;
  }
  // Fallback: prepend the existing value so the Select renders correctly even
  // if it isn't one of the canonical four.
  return [{ value: currentValue, label: currentValue }, ...STANDARD_ID_TYPES];
}

function userTypeLabel(t: string): string {
  if (t === 'clinician') return 'Practitioner';
  if (t === 'patient') return 'Patient';
  if (t === 'admin') return 'Org admin';
  if (t === 'user') return 'User';
  return t;
}

function formatErr(err: unknown): string | null {
  if (
    err &&
    typeof err === 'object' &&
    'body' in err &&
    typeof (err as { body: unknown }).body === 'string'
  ) {
    try {
      const parsed = JSON.parse((err as { body: string }).body);
      if (parsed?.message) return parsed.message as string;
    } catch {
      /* fall through */
    }
  }
  if (err instanceof Error && err.message) return err.message;
  return null;
}

function MembershipRow({
  userId,
  membership,
  onError,
}: {
  userId: string;
  membership: PlatformUserMembership;
  onError: (msg: string) => void;
}) {
  const [roleId, setRoleId] = useState(membership.role.id);
  const [confirmOpen, { open: openConfirm, close: closeConfirm }] = useDisclosure(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const update = useUpdatePlatformUserMembershipRole(userId, membership.id);
  const remove = useRemovePlatformUserMembership(userId, membership.id);
  const rolesQuery = useRolesForOrg(membership.organisation_id);
  const grantedIds = (membership.product_access ?? []).map((pa) => pa.product_id);

  const dirty = roleId !== membership.role.id;

  function handleSaveRole() {
    update.mutate(
      { role_id: roleId },
      {
        onError: (err) => onError(formatErr(err) ?? 'Could not update role.'),
      },
    );
  }

  function handleRemove() {
    remove.mutate(undefined, {
      onSuccess: () => closeConfirm(),
      onError: (err) => {
        onError(formatErr(err) ?? 'Could not remove membership.');
        closeConfirm();
      },
    });
  }

  return (
    <Table.Tr>
      <Table.Td>{membership.organisation_name ?? membership.organisation_id}</Table.Td>
      <Table.Td>
        <NativeSelect
          aria-label="Role"
          value={roleId}
          onChange={(e) => setRoleId(e.currentTarget.value)}
          data={(rolesQuery.data ?? [{ id: membership.role.id, name: membership.role.name }]).map(
            (r) => ({ value: r.id, label: r.name }),
          )}
          disabled={update.isPending || remove.isPending}
        />
      </Table.Td>
      <Table.Td>
        <Group gap="xs">
          <Badge color={grantedIds.length > 0 ? 'blue' : 'gray'} variant="light">
            {grantedIds.length} product{grantedIds.length === 1 ? '' : 's'}
          </Badge>
          <Button size="compact-xs" variant="light" onClick={() => setProductsOpen(true)}>
            Manage
          </Button>
        </Group>
      </Table.Td>
      <Table.Td>
        <Button
          size="compact-sm"
          variant="light"
          disabled={!dirty || update.isPending}
          loading={update.isPending}
          onClick={handleSaveRole}
        >
          Save role
        </Button>
      </Table.Td>
      <Table.Td>
        <Button
          size="compact-sm"
          color="red"
          variant="subtle"
          onClick={openConfirm}
          loading={remove.isPending}
        >
          Remove
        </Button>
        <Modal
          opened={confirmOpen}
          onClose={closeConfirm}
          title="Confirm removal"
          centered
          transitionProps={{ duration: 0 }}
        >
          <Stack>
            <Text fw={500}>
              Remove access to {membership.organisation_name ?? membership.organisation_id}?
            </Text>
            <Group justify="flex-end">
              <Button variant="default" onClick={closeConfirm}>
                Cancel
              </Button>
              <Button color="red" onClick={handleRemove} loading={remove.isPending}>
                Remove
              </Button>
            </Group>
          </Stack>
        </Modal>
        <MembershipProductsModal
          opened={productsOpen}
          onClose={() => setProductsOpen(false)}
          userId={userId}
          membershipId={membership.id}
          orgId={membership.organisation_id}
          orgName={membership.organisation_name ?? membership.organisation_id}
          grantedProductIds={grantedIds}
        />
      </Table.Td>
    </Table.Tr>
  );
}

export function PlatformUserEditPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const userQuery = usePlatformUser(id);
  const update = useUpdatePlatformUser(id);
  const deactivate = useDeactivatePlatformUser(id);
  const reactivate = useReactivatePlatformUser(id);
  const resetPw = useResetPlatformUserPassword(id);

  const [displayName, setDisplayName] = useState('');
  const [profId, setProfId] = useState('');
  const [profIdType, setProfIdType] = useState('');
  const [speciality, setSpeciality] = useState('');
  const [credentials, setCredentials] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [membershipsError, setMembershipsError] = useState<string | null>(null);
  const [addOrgId, setAddOrgId] = useState('');
  const [addRoleId, setAddRoleId] = useState('');

  const [confirmDeactivate, { open: openDeact, close: closeDeact }] = useDisclosure(false);
  const [confirmReactivate, { open: openReact, close: closeReact }] = useDisclosure(false);
  const [confirmReset, { open: openReset, close: closeReset }] = useDisclosure(false);

  const user = userQuery.data;

  const addMutation = useAddPlatformUserMembership(user?.id ?? '');
  const { data: orgs } = useOrgs();
  const addRolesQuery = useRolesForOrg(addOrgId || undefined);

  const memberOrgIds = new Set(user?.memberships.map((m) => m.organisation_id) ?? []);
  const availableOrgs = (orgs ?? []).filter((o) => !memberOrgIds.has(o.id));

  useEffect(() => {
    if (!user) return;
    setDisplayName(user.display_name);
    if (user.user_type === 'clinician' && user.practitioner) {
      setProfId(user.practitioner.professional_id ?? '');
      setProfIdType(user.practitioner.professional_id_type ?? '');
      setSpeciality(user.practitioner.speciality ?? '');
      setCredentials(user.practitioner.credentials ?? '');
    }
  }, [user?.id]);

  if (userQuery.isPending) return <Text p="md">Loading…</Text>;
  if (userQuery.isError || !user) return <Text p="md">Could not load user.</Text>;

  const dirty =
    (user.display_name !== displayName && displayName.trim().length > 0) ||
    (user.user_type === 'clinician' &&
      (user.practitioner?.professional_id !== profId ||
        user.practitioner?.professional_id_type !== profIdType ||
        user.practitioner?.speciality !== speciality ||
        (user.practitioner?.credentials ?? '') !== credentials));

  function handleSave(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);

    const body: UpdatePlatformUserInput = {};
    if (user!.display_name !== displayName) body.display_name = displayName;
    if (user!.user_type === 'clinician' && user!.practitioner) {
      if (user!.practitioner.professional_id !== profId) body.professional_id = profId;
      if (user!.practitioner.professional_id_type !== profIdType)
        body.professional_id_type = profIdType;
      if (user!.practitioner.speciality !== speciality) body.speciality = speciality;
      if ((user!.practitioner.credentials ?? '') !== credentials) body.credentials = credentials;
    }

    update.mutate(body, {
      onSuccess: () => notifications.show({ color: 'green', message: 'Profile updated.' }),
      onError: () => setError('Could not save changes.'),
    });
  }

  function handleDeactivate() {
    deactivate.mutate(undefined, {
      onSuccess: () => {
        notifications.show({ color: 'green', message: `${user!.email} deactivated.` });
        closeDeact();
      },
      onError: () => setError('Could not deactivate.'),
    });
  }

  function handleReactivate() {
    reactivate.mutate(undefined, {
      onSuccess: () => {
        notifications.show({ color: 'green', message: `${user!.email} reactivated.` });
        closeReact();
      },
      onError: () => setError('Could not reactivate.'),
    });
  }

  function handleReset() {
    resetPw.mutate(undefined, {
      onSuccess: () => {
        notifications.show({ color: 'green', message: 'Password reset email sent.' });
        closeReset();
      },
      onError: () => setError('Could not send reset email.'),
    });
  }

  function handleAdd() {
    if (!addOrgId || !addRoleId) return;
    addMutation.mutate(
      { organisation_id: addOrgId, role_id: addRoleId },
      {
        onSuccess: () => {
          setAddOrgId('');
          setAddRoleId('');
        },
        onError: (err) => {
          setMembershipsError(formatErr(err) ?? 'Could not add membership.');
        },
      },
    );
  }

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-end">
        <Title order={2}>{user.email}</Title>
        <Button variant="subtle" onClick={() => navigate('/users')}>
          Back to list
        </Button>
      </Group>

      {error && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          {error}
        </Alert>
      )}

      <Card withBorder padding="lg" maw={680}>
        <Group gap="lg">
          <div>
            <Text size="sm" c="dimmed">
              Type
            </Text>
            <Text fw={500}>{userTypeLabel(user.user_type)}</Text>
          </div>
          <div>
            <Text size="sm" c="dimmed">
              Status
            </Text>
            <Badge color={user.status === 'active' ? 'green' : 'gray'} variant="light">
              {user.status}
            </Badge>
          </div>
          <div>
            <Text size="sm" c="dimmed">
              Last updated
            </Text>
            <Text>{new Date(user.updated_at).toLocaleString()}</Text>
          </div>
        </Group>
      </Card>

      <Card withBorder padding="lg" maw={680}>
        <Box component="form" onSubmit={handleSave}>
          <Stack gap="md">
            <Title order={5}>Profile</Title>
            <TextInput
              label="Display name"
              required
              value={displayName}
              onChange={(e) => setDisplayName(e.currentTarget.value)}
              disabled={update.isPending}
            />
            {user.user_type === 'clinician' && (
              <>
                <TextInput
                  label="Professional ID"
                  required
                  value={profId}
                  onChange={(e) => setProfId(e.currentTarget.value)}
                  disabled={update.isPending}
                />
                <NativeSelect
                  label="Professional ID type"
                  required
                  value={profIdType}
                  onChange={(e) => setProfIdType(e.currentTarget.value)}
                  disabled={update.isPending}
                  data={profIdTypeOptions(profIdType)}
                />
                <TextInput
                  label="Speciality"
                  required
                  value={speciality}
                  onChange={(e) => setSpeciality(e.currentTarget.value)}
                  disabled={update.isPending}
                />
                <TextInput
                  label="Credentials (optional)"
                  value={credentials}
                  onChange={(e) => setCredentials(e.currentTarget.value)}
                  disabled={update.isPending}
                />
              </>
            )}
            <Group justify="flex-end">
              <Button type="submit" loading={update.isPending} disabled={!dirty}>
                Save changes
              </Button>
            </Group>
          </Stack>
        </Box>
      </Card>

      <Card withBorder padding="md">
        <Stack>
          <Title order={5}>Memberships</Title>
          {membershipsError && (
            <Alert
              color="red"
              title="Membership error"
              onClose={() => setMembershipsError(null)}
              withCloseButton
            >
              {membershipsError}
            </Alert>
          )}
          {user.memberships.length === 0 ? (
            <Text c="dimmed">No org memberships. Add one below.</Text>
          ) : (
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Organisation</Table.Th>
                  <Table.Th>Role</Table.Th>
                  <Table.Th>Products</Table.Th>
                  <Table.Th></Table.Th>
                  <Table.Th></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {user.memberships.map((m) => (
                  <MembershipRow
                    key={m.id}
                    userId={user.id}
                    membership={m}
                    onError={setMembershipsError}
                  />
                ))}
              </Table.Tbody>
            </Table>
          )}

          <Box role="region" aria-label="Add to organisation">
            <Title order={6}>Add to organisation</Title>
            <Group align="flex-end">
              <NativeSelect
                label="Organisation"
                value={addOrgId}
                onChange={(e) => {
                  setAddOrgId(e.currentTarget.value);
                  setAddRoleId('');
                }}
                data={[
                  { value: '', label: 'Select an org' },
                  ...availableOrgs.map((o) => ({ value: o.id, label: o.name })),
                ]}
              />
              <NativeSelect
                label="Role"
                value={addRoleId}
                onChange={(e) => setAddRoleId(e.currentTarget.value)}
                disabled={!addOrgId}
                data={[
                  { value: '', label: addOrgId ? 'Pick a role' : 'Pick an org first' },
                  ...(addRolesQuery.data ?? []).map((r) => ({ value: r.id, label: r.name })),
                ]}
              />
              <Button
                onClick={handleAdd}
                disabled={!addOrgId || !addRoleId || addMutation.isPending}
                loading={addMutation.isPending}
              >
                Add
              </Button>
            </Group>
          </Box>
        </Stack>
      </Card>

      <NotificationPreferencesCard
        userId={user.id}
        orgId={user.memberships[0]?.organisation_id ?? ''}
      />

      <Card withBorder padding="lg" maw={680}>
        <Stack gap="sm">
          <Title order={5}>Account actions</Title>
          {user.status === 'active' && (
            <Group>
              <Button color="yellow" onClick={openReset} loading={resetPw.isPending}>
                Reset password
              </Button>
              <Button color="red" onClick={openDeact} loading={deactivate.isPending}>
                Deactivate
              </Button>
            </Group>
          )}
          {user.status === 'deactivated' && (
            <Button color="green" onClick={openReact} loading={reactivate.isPending}>
              Reactivate
            </Button>
          )}
        </Stack>
      </Card>

      <Modal
        opened={confirmReset}
        onClose={closeReset}
        title="Reset password"
        transitionProps={{ duration: 0 }}
      >
        <Stack>
          <Text>Send a Cognito password-reset email to {user.email}?</Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeReset}>
              Cancel
            </Button>
            <Button color="yellow" onClick={handleReset} loading={resetPw.isPending}>
              Send reset email
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={confirmDeactivate}
        onClose={closeDeact}
        title="Deactivate user"
        transitionProps={{ duration: 0 }}
      >
        <Stack>
          <Text>Deactivate {user.email}? They will no longer be able to sign in.</Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeDeact}>
              Cancel
            </Button>
            <Button color="red" onClick={handleDeactivate} loading={deactivate.isPending}>
              Deactivate
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={confirmReactivate}
        onClose={closeReact}
        title="Reactivate user"
        transitionProps={{ duration: 0 }}
      >
        <Stack>
          <Text>
            Reactivate {user.email}? They'll be able to sign in again with their existing password.
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeReact}>
              Cancel
            </Button>
            <Button color="green" onClick={handleReactivate} loading={reactivate.isPending}>
              Reactivate
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
